import { useEffect, useState, useCallback } from 'react';
import { Stage, Layer, Rect as KonvaRect } from 'react-konva';
import { useGameStore } from '../store/gameStore';
import type { Card } from '../types';
import { HandCards } from '../components/HandCards';
import { PlayedCards } from '../components/PlayedCards';
import { PlayerSeat } from '../components/PlayerSeat';
import { GameInfo } from '../components/GameInfo';
import { GENRE_NAMES, CardGenre } from '../types';

const STAGE_WIDTH = 1200;
const STAGE_HEIGHT = 800;

const LEVEL_NAMES: Record<number, string> = {
  2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7',
  8: '8', 9: '9', 10: '10', 11: 'J', 12: 'Q', 13: 'K', 14: 'A', 15: '2',
};

export function GamePage() {
  const { currentRoom, ws, myPosition, setGameState, setPage, setWs } = useGameStore();
  const [selectedCards, setSelectedCards] = useState<Card[]>([]);

  const players = currentRoom?.players || [];
  const gameState = useGameStore(s => s.gameState);
  const currentTurn = currentRoom?.currentTurn ?? -1;
  const isMyTurn = currentTurn === myPosition;
  const currentLevel = currentRoom?.currentLevel || 2;

  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event: MessageEvent) => {
      const msg = JSON.parse(event.data);
      switch (msg.type) {
        case 'game_started':
        case 'player_played':
        case 'player_passed':
        case 'turn_change':
          setGameState({
            currentGenre: msg.data.genre || 0,
            currentCards: msg.data.cards || [],
            lastPlayer: msg.data.seat ?? msg.data.currentSeat ?? null,
            lastGenre: msg.data.genre ?? null,
            lastCards: msg.data.cards || [],
            isFirstRound: !msg.data.genre,
          });
          break;
        case 'room_player_left':
        case 'error':
          alert(msg.data.message || '游戏结束');
          ws.close();
          setWs(null);
          setPage('hall');
          break;
      }
    };

    ws.addEventListener('message', handleMessage);
    return () => {
      ws.removeEventListener('message', handleMessage);
      ws.close();
    };
  }, [ws, setGameState, setPage, setWs]);

  const handleCardSelect = useCallback((card: Card) => {
    setSelectedCards(prev => {
      const exists = prev.find(c => c.id === card.id);
      if (exists) {
        return prev.filter(c => c.id !== card.id);
      }
      return [...prev, card];
    });
  }, []);

  const handlePlay = () => {
    if (!ws || selectedCards.length === 0) return;
    ws.send(JSON.stringify({
      type: 'cards_play',
      data: { cardIds: selectedCards.map(c => c.id) },
    }));
    setSelectedCards([]);
  };

  const handlePass = () => {
    if (!ws) return;
    ws.send(JSON.stringify({ type: 'cards_pass', data: {} }));
  };

  const handleExit = () => {
    if (ws) ws.close();
    setWs(null);
    setPage('hall');
  };

  const myPlayer = players[myPosition];
  const myCards = myPlayer?.hand || [];

  const getRelativePlayers = () => {
    const result: (typeof players[0] | null)[] = [null, null, null, null];
    for (let i = 0; i < 4; i++) {
      const relPos = (i - myPosition + 4) % 4;
      result[relPos] = players[i] || null;
    }
    return result;
  };

  const relPlayers = getRelativePlayers();
  const relTurn = (currentTurn - myPosition + 4) % 4;

  return (
    <div className="min-h-screen bg-green-800 p-4">
      <div className="max-w-[1200px] mx-auto">
        <div className="bg-white rounded-xl p-2 mb-2 flex justify-between items-center">
          <div>
            <span className="font-bold">房间: {currentRoom?.name}</span>
            <span className="ml-4">当前级数: {LEVEL_NAMES[currentLevel] || currentLevel}</span>
          </div>
          <button
            onClick={handleExit}
            className="bg-red-500 text-white px-4 py-1 rounded-lg"
          >
            退出
          </button>
        </div>

        <div className="bg-green-700 rounded-xl overflow-hidden shadow-2xl">
          <Stage width={STAGE_WIDTH} height={STAGE_HEIGHT}>
            <Layer>
              <KonvaRect width={STAGE_WIDTH} height={STAGE_HEIGHT} fill="#2d5a3d" />
              
              <PlayerSeat
                x={STAGE_WIDTH / 2}
                y={80}
                player={relPlayers[1]}
                isTurn={relTurn === 1}
                position="top"
              />
              <PlayerSeat
                x={STAGE_WIDTH - 150}
                y={STAGE_HEIGHT / 2}
                player={relPlayers[2]}
                isTurn={relTurn === 2}
                position="right"
              />
              <PlayerSeat
                x={STAGE_WIDTH / 2}
                y={STAGE_HEIGHT - 80}
                player={relPlayers[3]}
                isTurn={relTurn === 3}
                position="bottom"
              />
              <PlayerSeat
                x={150}
                y={STAGE_HEIGHT / 2}
                player={relPlayers[0]}
                isTurn={relTurn === 0}
                position="left"
              />

              <PlayedCards
                cards={gameState?.lastCards || []}
                genre={gameState?.lastGenre}
                x={STAGE_WIDTH / 2}
                y={STAGE_HEIGHT / 2}
              />

              <GameInfo
                genre={gameState?.currentGenre || 0}
                level={currentLevel}
                x={STAGE_WIDTH / 2}
                y={STAGE_HEIGHT / 2 - 80}
              />

              <HandCards
                cards={myCards}
                selectedCards={selectedCards}
                onSelect={handleCardSelect}
                x={STAGE_WIDTH / 2}
                y={STAGE_HEIGHT - 120}
              />
            </Layer>
          </Stage>
        </div>

        <div className="flex justify-center gap-4 mt-4">
          <button
            onClick={handlePass}
            disabled={!isMyTurn || gameState?.isFirstRound}
            className="bg-gray-500 hover:bg-gray-600 text-white px-8 py-3 rounded-xl font-semibold disabled:opacity-50"
          >
            不出
          </button>
          <button
            onClick={handlePlay}
            disabled={!isMyTurn || selectedCards.length === 0}
            className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-xl font-semibold disabled:opacity-50"
          >
            出牌
          </button>
        </div>
      </div>
    </div>
  );
}